import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, MagnifyingGlass, X, Flask, ChatCircle, BookmarkSimple, Path, Clock, CheckCircle, Microphone, Newspaper, CaretDown } from '@phosphor-icons/react';
import AppNav from '../components/AppNav';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';
import JsonLd, { collectionPageSchema } from '../components/JsonLd';
import TopicCard from '../components/library/TopicCard';
import ConditionFilterPills from '../components/library/ConditionFilterPills';
import LoginModal from '../components/LoginModal';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../config/api';
import { getTopics, getConditions, searchTopics, getBookmarks, getLearningPaths, getTopicViews, type TopicListItem, type ConditionItem, type LearningPathListItem } from '../utils/library';
import type { UserBookmark } from '@researcher/shared';

interface FeaturedArticle {
  id: string;
  title: string;
  journal: string;
  evidenceType: string;
  qualityScore: number;
  conditionTags: string[];
  publishedDate: string;
  sourceUrl: string;
}

interface PodcastEpisode {
  id: string;
  title: string;
  description: string;
  showName: string;
  sourceUrl: string;
  date: string;
}

export default function Library() {
  const { currentUser } = useAuth();
  const [topics, setTopics] = useState<TopicListItem[]>([]);
  const [conditions, setConditions] = useState<ConditionItem[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<FeaturedArticle[]>([]);
  const [podcastEpisodes, setPodcastEpisodes] = useState<PodcastEpisode[]>([]);
  const [bookmarks, setBookmarks] = useState<UserBookmark[]>([]);
  const [learningPaths, setLearningPaths] = useState<LearningPathListItem[]>([]);
  const [viewedSlugs, setViewedSlugs] = useState<Set<string>>(new Set());
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TopicListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);

  // Load topics and conditions
  useEffect(() => {
    const load = async () => {
      try {
        const [topicsData, conditionsData] = await Promise.all([
          getTopics({ limit: 50 }),
          getConditions(),
        ]);
        setTopics(topicsData.topics);
        setConditions(conditionsData);
      } catch (error) {
        console.error('Error loading library:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load bookmarks (logged-in users)
  useEffect(() => {
    if (!currentUser) { setBookmarks([]); return; }
    getBookmarks().then(setBookmarks).catch(console.error);
  }, [currentUser]);

  // Load viewed topic slugs (logged-in users)
  useEffect(() => {
    if (!currentUser) { setViewedSlugs(new Set()); return; }
    getTopicViews()
      .then(slugs => setViewedSlugs(new Set(slugs)))
      .catch(console.error);
  }, [currentUser]);

  // Load learning paths
  useEffect(() => {
    getLearningPaths().then(setLearningPaths).catch(console.error);
  }, []);

  // Load featured research articles (PubMed daily picks)
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await fetch(apiUrl('/library/featured-research'));
        if (response.ok) {
          const data = await response.json();
          setFeaturedArticles(data.data?.articles || []);
        }
      } catch (error) {
        console.error('Error fetching featured research:', error);
      }
    };
    fetchFeatured();
  }, []);

  // Load podcast episodes
  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        const response = await fetch(apiUrl('/podcasts?limit=6'));
        if (response.ok) {
          const data = await response.json();
          setPodcastEpisodes(data.data?.episodes || []);
        }
      } catch (error) {
        console.error('Error fetching podcasts:', error);
      }
    };
    fetchPodcasts();
  }, []);

  // Filter by condition — reset expand when switching
  useEffect(() => {
    setShowAllTopics(false);
    if (selectedCondition === null) {
      // Reset to all topics
      setLoading(true);
      getTopics({ limit: 50 })
        .then(data => setTopics(data.topics))
        .catch(console.error)
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    getTopics({ condition: selectedCondition, limit: 50 })
      .then(data => setTopics(data.topics))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedCondition]);

  // Search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchTopics(searchQuery.trim());
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const FEATURED_SLUGS = ['endometriosis', 'pcos', 'perimenopause', 'pmdd', 'anxiety-women', 'contraception'];
  const isFiltered = !!searchQuery || !!selectedCondition;
  const allTopics = searchResults ?? topics;

  // When showing the default "All Topics" view, surface featured slugs first
  const sortedTopics = isFiltered ? allTopics : [
    ...FEATURED_SLUGS.map(s => allTopics.find(t => t.slug === s)).filter(Boolean) as TopicListItem[],
    ...allTopics.filter(t => !FEATURED_SLUGS.includes(t.slug)),
  ];

  const COLLAPSED_COUNT = 6;
  const displayTopics = (isFiltered || showAllTopics) ? sortedTopics : sortedTopics.slice(0, COLLAPSED_COUNT);
  const hasMoreTopics = !isFiltered && !showAllTopics && sortedTopics.length > COLLAPSED_COUNT;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return 'Today';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title="ResearcHer Library — Evidence-Based Women's Health"
        description="Browse evidence-based clinical topics on endometriosis, PCOS, fertility, menopause, and more. Powered by PubMed research and expert synthesis."
        path="/research"
      />
      <JsonLd data={collectionPageSchema({ name: 'ResearchHer Library', description: 'Evidence-based clinical research library for women\'s health conditions including endometriosis, PCOS, fertility, menopause, and more.', url: 'https://romyhealth.co/research' })} />
      <AppNav onLoginOpen={() => setIsLoginOpen(true)} />

      <main className="pt-16 pb-16 md:pb-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          {/* Hero */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                <BookOpen size={30} weight="fill" className="text-white" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              <span className="text-gray-800 dark:text-gray-100">ResearcHer</span>{' '}
              <span className="text-pink-500">Library</span>
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
              Evidence-based clinical topics, synthesized from PubMed research.
              Every article is grounded in peer-reviewed evidence with full citations.
            </p>
          </div>

          {/* Reading progress */}
          {currentUser && viewedSlugs.size > 0 && topics.length > 0 && !searchQuery && (
            <div className="max-w-lg mx-auto mb-6 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                You've explored {viewedSlugs.size} of {topics.length} topics
              </p>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, Math.round((viewedSlugs.size / topics.length) * 100))}%` }}
                />
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative max-w-lg mx-auto mb-6">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-pink-500"
            />
            <input
              type="text"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-900 border-2 border-pink-200 dark:border-pink-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 shadow-sm text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-pink-400 hover:text-pink-600"
              >
                <X size={18} weight="bold" />
              </button>
            )}
          </div>

          {/* Condition filter pills */}
          {conditions.length > 0 && !searchQuery && (
            <ConditionFilterPills
              conditions={conditions}
              selected={selectedCondition}
              onSelect={setSelectedCondition}
            />
          )}

          {/* Search status */}
          {searching && (
            <div className="text-center py-4">
              <div className="w-5 h-5 border-2 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          )}

          {searchQuery && searchResults !== null && !searching && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {searchResults.length === 0
                ? `No topics found for "${searchQuery}"`
                : `${searchResults.length} topic${searchResults.length !== 1 ? 's' : ''} found`
              }
            </p>
          )}

          {/* Saved Topics */}
          {bookmarks.length > 0 && !searchQuery && (
            <section className="mb-8 mt-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <BookmarkSimple size={20} weight="fill" className="text-pink-500" />
                Saved Topics
              </h2>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {bookmarks.map(b => (
                  <Link
                    key={b.id}
                    to={`/research/topics/${b.topicSlug}`}
                    className="flex-shrink-0 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-3 hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 line-clamp-2">{b.topicTitle}</h3>
                    <p className="text-[11px] text-gray-400 mt-1">{b.topicSlug.replace(/-/g, ' ')}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Topics Grid */}
          <section className="mb-10 mt-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
              {searchQuery ? 'Search Results' : selectedCondition ? `Topics: ${selectedCondition.replace(/-/g, ' ')}` : 'All Topics'}
            </h2>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-1" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : displayTopics.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {displayTopics.map(topic => (
                    <TopicCard key={topic.slug} topic={topic} isRead={viewedSlugs.has(topic.slug)} />
                  ))}
                </div>
                {hasMoreTopics && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => setShowAllTopics(true)}
                      className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-950/30 hover:bg-pink-100 dark:hover:bg-pink-900/30 rounded-lg transition-colors"
                    >
                      <span>View all {sortedTopics.length} topics</span>
                      <CaretDown size={16} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                <BookOpen size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-1 font-medium">No topics yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Clinical topics will appear here once the library is populated.
                </p>
              </div>
            )}
          </section>

          {/* Learning Paths */}
          {learningPaths.length > 0 && !searchQuery && (
            <section className="mb-10">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Path size={20} weight="bold" className="text-orange-500" />
                Learning Paths
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {learningPaths.map(lp => {
                  const isPathComplete = currentUser && lp.steps.length > 0 && lp.steps.every(s => viewedSlugs.has(s.topicSlug));
                  return (
                    <Link
                      key={lp.slug}
                      to={`/research/learning/${lp.slug}`}
                      className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 font-medium">
                          {lp.difficulty}
                        </span>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          <Clock size={12} /> {lp.totalReadTimeMinutes} min
                        </span>
                        {isPathComplete && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 font-medium flex items-center gap-0.5">
                            <CheckCircle size={10} weight="fill" /> Completed
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">{lp.title}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{lp.description}</p>
                      <p className="text-[11px] text-gray-400 mt-2">{lp.steps.length} topics</p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Clinical Trials CTA */}
          <section className="mb-10">
            <div className="bg-gradient-to-r from-orange-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 rounded-xl border border-orange-200 dark:border-orange-800 p-6 text-center">
              <Flask size={36} className="text-orange-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                Find Clinical Trials
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-lg mx-auto">
                Search ClinicalTrials.gov for recruiting studies or get personalized matches.
              </p>
              <Link
                to="/trials"
                className="inline-block px-6 py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-sm text-sm"
              >
                Search Trials
              </Link>
            </div>
          </section>

          {/* Ask Romy CTA */}
          <section className="mb-10">
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-xl border border-pink-200 dark:border-pink-800 p-6 text-center">
              <ChatCircle size={36} className="text-pink-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                Ask Romy for a Deep Dive
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-lg mx-auto">
                Ask Romy to research any women's health question with full evidence citations.
              </p>
              <Link
                to="/chat"
                className="inline-block px-6 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-medium rounded-lg hover:from-pink-600 hover:to-purple-600 transition-all shadow-sm text-sm"
              >
                Chat with Romy
              </Link>
            </div>
          </section>

          {/* Featured Research (PubMed daily picks) */}
          {featuredArticles.length > 0 && (
            <section className="mb-10">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Newspaper size={20} weight="fill" className="text-purple-500" />
                Featured Research
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {featuredArticles.slice(0, 6).map(article => (
                  <a
                    key={article.id}
                    href={article.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-shadow block"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 font-medium">
                        {article.evidenceType}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 font-medium">
                        Quality: {article.qualityScore}/10
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1 line-clamp-2">
                      {article.title}
                    </h3>
                    <div className="flex items-center justify-between text-[11px] text-gray-400 mt-2">
                      <span>{formatDate(article.publishedDate)}</span>
                      <span className="truncate ml-2">{article.journal}</span>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Podcasts */}
          {podcastEpisodes.length > 0 && (
            <section className="mb-10">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Microphone size={20} weight="fill" className="text-pink-500" />
                Latest Episodes
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {podcastEpisodes.map(ep => (
                  <a
                    key={ep.id}
                    href={ep.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-shadow block"
                  >
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-100 dark:bg-pink-950 text-pink-700 dark:text-pink-300 font-medium">
                      {ep.showName}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-2 mb-1 line-clamp-2">
                      {ep.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                      {ep.description?.substring(0, 120)}
                    </p>
                    <div className="text-[11px] text-gray-400">
                      {formatDate(ep.date)}
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <AppFooter />
      {isLoginOpen && <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />}
    </div>
  );
}
